import flatpickr from 'flatpickr';
import flatpickrLocales from 'flatpickr/dist/l10n/index.js';
import 'flatpickr/dist/flatpickr.min.css';
import * as travelUtils from 'travelAssetsJsPath/utils.js';
import { onElementReady } from "travelAssetsJsPath/utils.js";
import {
    FLATPICKR_RANGE_SEPARATOR,
    getFlatpickrFirstDayOfWeekFromObject,
    resolveBrowserLocaleConfig,
} from './flatpickr-locale-utils';

let bookingFlatpickrLocaleReady = null;

const loadFlatpickrLocaleFromBrowser = async () => {
    const {
        localeCode: selectedLocaleCode,
        localeConfig: selectedLocaleConfig,
    } = resolveBrowserLocaleConfig(flatpickrLocales);

    if (selectedLocaleConfig) {
        flatpickr.localize(selectedLocaleConfig);
    }

    return {
        localeCode: selectedLocaleCode,
        localeConfig: selectedLocaleConfig,
    };
};

const setupBookingFlatpickrLocale = (firstDayOfWeek = 1) => {
    if (!bookingFlatpickrLocaleReady) {
        bookingFlatpickrLocaleReady = loadFlatpickrLocaleFromBrowser();
    }

    return bookingFlatpickrLocaleReady
        .catch(() => ({
            localeCode: 'default',
            localeConfig: null,
        }))
        .then((localeState) => {
            flatpickr.l10ns.default.firstDayOfWeek = firstDayOfWeek;
            flatpickr.l10ns.default.rangeSeparator = FLATPICKR_RANGE_SEPARATOR;

            return {
                ...localeState,
                localeConfig: { ...flatpickr.l10ns.default },
            };
        });
};

/**
 * TourBookingManager - Tour Booking Management Class
 * Vanilla JavaScript class-based architecture
 * @author physcode
 * @version 3.0.0
 */
class TourBookingManager {
    // ====================================================================
    // STATIC CONSTANTS
    // ====================================================================

    static DATE_FORMAT_DEFAULT = 'Y/m/d';
    static DATE_FORMAT_RANGE = 'YYYY/MM/DD';

    static DATE_FORMAT_MAP = {
        'Y/m/d': 'Y/m/d',
        'Y-m-d': 'Y-m-d',
        'd/m/Y': 'd/m/Y',
        'd-m-Y': 'd-m-Y',
        'm/d/Y': 'm/d/Y',
        'm-d-Y': 'm-d-Y',
        'D, d M, Y': 'D, j M, Y',
        'l, d F, Y': 'l, j F, Y',
    };

    // ====================================================================
    // STATIC UTILITY METHODS
    // ====================================================================

    /**
     * Safely parse a value to float
     * @param {*} value - Value to parse
     * @param {number} defaultValue - Default if parsing fails
     * @returns {number}
     */
    static safeParseFloat(value, defaultValue = 0) {
        if (value === null || value === undefined || value === '') {
            return defaultValue;
        }
        const parsed = parseFloat(value);
        return isNaN(parsed) ? defaultValue : parsed;
    }

    /**
     * Safely parse a value to integer
     * @param {*} value - Value to parse
     * @param {number} defaultValue - Default if parsing fails
     * @returns {number}
     */
    static safeParseInt(value, defaultValue = 0) {
        if (value === null || value === undefined || value === '') {
            return defaultValue;
        }
        const parsed = parseInt(value, 10);
        return isNaN(parsed) ? defaultValue : parsed;
    }

    /**
     * Get cookie value by name
     * @param {string} cname - Cookie name
     * @returns {string}
     */
    static getCookie(cname) {
        const name = cname + '=';
        const decodedCookie = decodeURIComponent(document.cookie);
        const ca = decodedCookie.split(';');
        for (let i = 0; i < ca.length; i++) {
            let c = ca[i];
            while (c.charAt(0) === ' ') {
                c = c.substring(1);
            }
            if (c.indexOf(name) === 0) {
                return c.substring(name.length, c.length);
            }
        }
        return '';
    }

    /**
     * Format price with proper separators
     * @param {number} val - Price value
     * @returns {string}
     */
    static formatPrice(val) {
        if (typeof travel_booking === 'undefined') return val;

        const decimals = TourBookingManager.safeParseInt(travel_booking.price_decimals, 2);
        const separator = travel_booking.price_thousands_separator || '.';
        const decimalSep = travel_booking.price_decimal_separator || ',';

        val = parseFloat(val) || 0;
        const parts = val.toFixed(decimals).split('.');
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, separator);
        return parts.join(decimalSep);
    }

    /**
     * Convert Date object to standard format string (YYYY/MM/DD)
     * @param {Date} dateObj - Date object to convert
     * @returns {string}
     */
    static convertDateObjToDefault(dateObj) {
        const year = dateObj.getFullYear();
        const month = ('0' + (dateObj.getMonth() + 1)).slice(-2);
        const date = ('0' + dateObj.getDate()).slice(-2);
        return year + '/' + month + '/' + date;
    }

    /**
     * Calculate total nights between two dates
     * @param {Date} startDate - Start date object
     * @param {Date} endDate - End date object
     * @returns {number}
     */
    static calculateTotalNights(startDate, endDate) {
        const dateNext = new Date(startDate.getTime());
        let totalDays = 0;
        while (dateNext < endDate) {
            dateNext.setDate(dateNext.getDate() + 1);
            totalDays += 1;
        }
        return totalDays;
    }

    // ====================================================================
    // CONSTRUCTOR
    // ====================================================================

    constructor() {
        // DOM Element References
        this.elements = {
            form: null,
            dateCheckinCheckout: null,
            dateRangePicker: null,
            dateRangePickerInput: null,
            priceTicket: null,
            showPriceTicket: null,
            numberTicket: null,
            numberChildren: null,
            priceChildren: null,
            showPriceChildren: null,
            fieldTravelBooking: null,
            inputDateBook: null,
            tourDateStartsAfter: null,
            tourDurationNumber: null,
            tourGroupDiscountDataPhys: null,
            labelTourGroupDiscount: null,
            tourDeposit: null,
            tourVariationsOptions: null,
            showTotalPrice: null,
            tourDateCheckIn: null,
            tourDateCheckOut: null,
        };

        // Flatpickr Instances
        this.pickers = {
            fixed: null,
            checkIn: null,
            checkOut: null,
        };

        // State
        this.state = {
            dateValidate: [],
            dateCanBook: [],
            tourMonth: [],
            tourHaveChildrenTicket: false,
            tourVariationOptions: null,
            tourGroupDiscountData: null,
            priceDatesTour: {},
            infoUser: null,
            numDecimals: 2,
            datesDisable: null,
            maxYear: null,
            tourDateFormat: null,
            flatpickrFirstDayOfWeek: 1,
            flatpickrLocale: null,
            tourDateCheckInOld: '',
            physTourPriceDatesType: null,
            tourPriceOfDatesOption: null,
            tourDatesChoose: [],
        };
    }

    // ====================================================================
    // INITIALIZATION METHODS
    // ====================================================================

    /**
     * Initialize the tour booking system
     */
    init() {
        // Check and attach events only once
        if ( TourBookingManager._inited ) {
            return;
        }

        TourBookingManager._inited = this;

        this.elements.form = document.querySelector('form#tourBookingForm');

        const cookieInfo = TourBookingManager.getCookie('info_user');
        if (cookieInfo) {
            try {
                this.state.infoUser = JSON.parse(cookieInfo);
            } catch (e) {
                this.state.infoUser = null;
            }
        }

        if (this.elements.form) {
            this.initElements();
            this.initDatepickers();
            this.initEventListeners();

            if (this.state.infoUser) {
                this._fillUserInfo();
            }
        }

        this.setupFormSubmission();
    }

    /**
     * Get references to all form elements
     */
    initElements() {
        const form = this.elements.form;

        this.elements.priceTicket = form.querySelector('input[name=price_ticket]');
        this.elements.numberTicket = form.querySelector('input[name=number_ticket]');
        this.elements.showPriceTicket = form.querySelector('.price_ticket');
        this.elements.numberChildren = form.querySelector('input[name=number_children]');
        this.elements.priceChildren = form.querySelector('input[name=price_children]');
        this.elements.fieldTravelBooking = form.querySelectorAll('.field-travel-booking');
        this.elements.showPriceChildren = form.querySelector('.price_child');
        this.elements.dateCheckinCheckout = form.querySelector('.tour_date_checkin_checkout');
        this.elements.inputDateBook = form.querySelector('input[name=date_book]');
        this.elements.tourDateStartsAfter = form.querySelector('input[name=tour_date_starts_after]');
        this.elements.tourDurationNumber = form.querySelector('input[name=tour_duration_number]');
        this.elements.tourGroupDiscountDataPhys = form.querySelector('input[name=tour_group_discount_data_phys]');
        this.elements.labelTourGroupDiscount = form.querySelector('.label-tour-group-discount');
        this.elements.tourDeposit = form.querySelectorAll('input[name=webtomizer_tour_deposit]');
        this.elements.tourVariationsOptions = form.querySelector('input[name=_tour_variations_options]');

        const tourMonthEl = form.querySelector('input[name=tour_month]');
        this.elements.showTotalPrice = form.querySelector('.tour-subtotal');

        const numDecimalsEl = document.querySelector('input[name=woocommerce_price_num_decimals]');
        this.state.numDecimals = numDecimalsEl ? TourBookingManager.safeParseInt(numDecimalsEl.value, 2) : 2;

        if (this.elements.numberChildren) {
            this.state.tourHaveChildrenTicket = true;
        }

        if (tourMonthEl && tourMonthEl.value) {
            this.state.tourMonth = tourMonthEl.value.split(',');
        }

        // Parse variation and discount data
        if (this.elements.tourVariationsOptions) {
            try {
                this.state.tourVariationOptions = JSON.parse(this.elements.tourVariationsOptions.value);
            } catch (e) {
                this.state.tourVariationOptions = null;
            }
        }

        if (this.elements.tourGroupDiscountDataPhys) {
            try {
                this.state.tourGroupDiscountData = JSON.parse(this.elements.tourGroupDiscountDataPhys.value);
            } catch (e) {
                this.state.tourGroupDiscountData = null;
            }
        }
    }

    /**
     * Re-query critical elements that may have been null during initial
     * initElements() due to async script loading race condition.
     * Called lazily before price calculation to ensure elements are found
     * once the DOM is fully parsed.
     */
    _refreshElements() {
        const form = this.elements.form;
        if (!form) return;

        if (!this.elements.priceTicket) {
            this.elements.priceTicket = form.querySelector('input[name=price_ticket]');
        }
        if (!this.elements.numberTicket) {
            this.elements.numberTicket = form.querySelector('input[name=number_ticket]');
        }
        if (!this.elements.numberChildren) {
            this.elements.numberChildren = form.querySelector('input[name=number_children]');
            if (this.elements.numberChildren) {
                this.state.tourHaveChildrenTicket = true;
            }
        }
        if (!this.elements.priceChildren) {
            this.elements.priceChildren = form.querySelector('input[name=price_children]');
        }
        if (!this.elements.showPriceTicket) {
            this.elements.showPriceTicket = form.querySelector('.price_ticket');
        }
        if (!this.elements.showPriceChildren) {
            this.elements.showPriceChildren = form.querySelector('.price_child');
        }
        if (!this.elements.showTotalPrice) {
            this.elements.showTotalPrice = form.querySelector('.tour-subtotal');
        }
        if (!this.elements.tourGroupDiscountDataPhys) {
            this.elements.tourGroupDiscountDataPhys = form.querySelector('input[name=tour_group_discount_data_phys]');
            if (this.elements.tourGroupDiscountDataPhys) {
                try {
                    this.state.tourGroupDiscountData = JSON.parse(this.elements.tourGroupDiscountDataPhys.value);
                } catch (e) {
                    this.state.tourGroupDiscountData = null;
                }
            }
        }
        if (!this.elements.labelTourGroupDiscount) {
            this.elements.labelTourGroupDiscount = form.querySelector('.label-tour-group-discount');
        }
        if (!this.elements.tourVariationsOptions) {
            this.elements.tourVariationsOptions = form.querySelector('input[name=_tour_variations_options]');
            if (this.elements.tourVariationsOptions) {
                try {
                    this.state.tourVariationOptions = JSON.parse(this.elements.tourVariationsOptions.value);
                } catch (e) {
                    this.state.tourVariationOptions = null;
                }
            }
        }
    }

    /**
     * Initialize event listeners for form fields
     */
    initEventListeners() {
        if (!this.elements.form) return;

        const self = this;

        /**
         * Determine whether a delegated event target should trigger
         * a price recalculation. Works for any element inside the form
         * regardless of when it was added to the DOM (async-safe).
         */
        const shouldRecalculate = (target) => {
            const inputType = target.type?.toLowerCase();
            const tagName = target.tagName.toLowerCase();

            // .field-travel-booking elements (date, quantity, etc.)
            if (target.classList.contains('field-travel-booking')) return true;

            // Any number input inside the form (number_ticket, number_children, variation-qty)
            if (inputType === 'number') return true;

            // Select dropdowns (variations)
            if (tagName === 'select') return true;

            // Radio / Checkbox inputs (variations, deposit option)
            if (inputType === 'radio' || inputType === 'checkbox') return true;

            return false;
        };

        // 'input' fires immediately on number spinners (up/down arrows)
        // and on every keystroke in text/number fields — gives real-time
        // feedback without waiting for blur ('change') or key release.
        this.elements.form.addEventListener('input', (e) => {
            if (shouldRecalculate(e.target)) {
                self.updateTotalPrice();
            }
        });

        // 'change' fires on blur for text/number, on selection for
        // select/radio/checkbox, and when flatpickr sets a value.
        this.elements.form.addEventListener('change', (e) => {
            if (shouldRecalculate(e.target)) {
                self.updateTotalPrice();
            }
        });
    }

    /**
     * Fill user info from cookies into form fields
     */
    _fillUserInfo() {
        const info = this.state.infoUser;
        const firstNameEl = document.querySelector('input[name=first_name]');
        const lastNameEl = document.querySelector('input[name=last_name]');
        const emailEl = document.querySelector('input[name=email_tour]');
        const phoneEl = document.querySelector('input[name=phone]');

        if (firstNameEl) firstNameEl.value = info.first_name || '';
        if (lastNameEl) lastNameEl.value = info.last_name || '';
        if (emailEl) emailEl.value = info.email || '';
        if (phoneEl) phoneEl.value = info.phone || '';
    }

    /**
     * Fill billing info from cookies
     */
    _fillBillingInfo() {
        const info = this.state.infoUser;
        const billingFirstName = document.querySelector('input[name=billing_first_name]');
        const billingLastName = document.querySelector('input[name=billing_last_name]');
        const billingEmail = document.querySelector('input[name=billing_email]');
        const billingPhone = document.querySelector('input[name=billing_phone]');

        if (billingFirstName) {
            this.setBillingValue( billingFirstName, info.first_name || '' );
        }
        if (billingLastName) {
            this.setBillingValue( billingLastName, info.last_name || '' );
        }
        if (billingEmail) {
            this.setBillingValue( billingEmail, info.email || '' );
        }
        if (billingPhone) {
            this.setBillingValue( billingPhone, info.phone || '' );
        }
    }

    isGutenbergCheckout() {
        return !! document.querySelector( '.wp-block-woocommerce-checkout' );
    }

    setBillingValue( el, value ) {
        if ( this.isGutenbergCheckout() ) {
            this.setInfoBillingLayoutGutenberg( el, value );
        } else {
            el.value = value;
        }
    }

    setInfoBillingLayoutGutenberg( el, value ) {
        const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
            window.HTMLInputElement.prototype,
            'value'
        ).set;

        nativeInputValueSetter.call( el, value );

        el.dispatchEvent( new Event( 'input', { bubbles: true } ) );
        el.dispatchEvent( new Event( 'change', { bubbles: true } ) );
    }

    // ====================================================================
    // DATE UTILITY METHODS
    // ====================================================================

    /**
     * Convert date string from custom format to YYYY/MM/DD
     * @param {string} dateString - Date in custom format
     * @returns {string}
     */
    convertDateToDefault(dateString) {
        if (!dateString || dateString === '') return '';

        let date = '', month = '', year = '';
        const format = this.state.tourDateFormat;

        if (format === 'Y/m/d') {
            const arr = dateString.split('/');
            [year, month, date] = arr;
        } else if (format === 'Y-m-d') {
            const arr = dateString.split('-');
            [year, month, date] = arr;
        } else if (format === 'd/m/Y') {
            const arr = dateString.split('/');
            [date, month, year] = arr;
        } else if (format === 'd-m-Y') {
            const arr = dateString.split('-');
            [date, month, year] = arr;
        } else if (format === 'm/d/Y') {
            const arr = dateString.split('/');
            [month, date, year] = arr;
        } else if (format === 'm-d-Y') {
            const arr = dateString.split('-');
            [month, date, year] = arr;
        } else if (format === 'D, j M, Y' || format === 'l, j F, Y') {
            const arr = dateString.split(',');
            const dateMonthArr = arr[1].trim().split(' ');
            date = dateMonthArr[0];
            month = this.state.tourMonth.indexOf(dateMonthArr[1]) + 1;
            if (parseInt(month) < 10) month = '0' + month;
            year = arr[2].trim();
        } else {
            // Fallback
            const nativeDate = new Date(dateString);
            if (!isNaN(nativeDate.getTime())) {
                year = nativeDate.getFullYear();
                month = String(nativeDate.getMonth() + 1).padStart(2, '0');
                date = String(nativeDate.getDate()).padStart(2, '0');
            }
        }

        if (!year || !month || !date) return dateString;
        return year + '/' + month + '/' + date;
    }

    getPickerLocaleConfig() {
        return {
            ...(this.state.flatpickrLocale || {}),
            firstDayOfWeek: this.state.flatpickrFirstDayOfWeek,
            rangeSeparator: FLATPICKR_RANGE_SEPARATOR,
        };
    }

    // ====================================================================
    // DATEPICKER METHODS
    // ====================================================================

    /**
     * Initialize all datepickers
     */
    initDatepickers() {
        const form = this.elements.form;
        let tourDateStartsAfter = 0;
        const dateNow = new Date();
        const dateMinCanBook = new Date();

        // Get configuration
        const elDatesDisable = form.querySelector('input[name=phys_tour_dates_disable]');
        const elMaxYear = form.querySelector('input[name=phys_tour_max_year_enable]');
        const elPriceDatesType = form.querySelector('input[name=phys_tour_price_dates_type]');
        const elPriceOfDatesOption = form.querySelector('input[name=phys_tour_price_of_dates_option]');

        this.state.physTourPriceDatesType = elPriceDatesType ? elPriceDatesType.value : null;
        this.state.tourDateFormat = TourBookingManager.DATE_FORMAT_DEFAULT;
        this.state.flatpickrFirstDayOfWeek = 1;

        if (typeof travel_booking !== 'undefined' && TourBookingManager.DATE_FORMAT_MAP[travel_booking.tour_date_format]) {
            this.state.tourDateFormat = TourBookingManager.DATE_FORMAT_MAP[travel_booking.tour_date_format];
        }

        if (typeof travel_booking !== 'undefined') {
            this.state.flatpickrFirstDayOfWeek = getFlatpickrFirstDayOfWeekFromObject(travel_booking, 1);
        }

        this.state.maxYear = elMaxYear ? elMaxYear.value : null;

        // Validate maxYear - ensure it's not less than current year
        if (this.state.maxYear) {
            const currentYear = new Date().getFullYear();
            const maxYear = parseInt(this.state.maxYear);
            if (maxYear < currentYear) {
                this.state.maxYear = currentYear.toString();
            }
        }

        if (elDatesDisable && elDatesDisable.value !== '') {
            try {
                this.state.datesDisable = JSON.parse(elDatesDisable.value);
            } catch (e) {
                this.state.datesDisable = null;
            }
        }

        if (elPriceOfDatesOption && elPriceOfDatesOption.value !== '' && elPriceOfDatesOption.value !== '{}') {
            try {
                this.state.tourPriceOfDatesOption = JSON.parse(elPriceOfDatesOption.value);
            } catch (e) {
                this.state.tourPriceOfDatesOption = null;
            }
        }

        // Calculate min bookable date
        if (this.elements.tourDateStartsAfter) {
            const val = this.elements.tourDateStartsAfter.value;
            if (val !== '' && parseInt(val) !== 0) {
                tourDateStartsAfter = parseInt(val);
            }
        }

        if (this.state.datesDisable !== null) {
            let dateMinStr = TourBookingManager.convertDateObjToDefault(dateMinCanBook);
            while (this.state.datesDisable.indexOf(dateMinStr) > -1) {
                dateMinCanBook.setDate(dateMinCanBook.getDate() + 1);
                dateMinStr = TourBookingManager.convertDateObjToDefault(dateMinCanBook);
            }
            dateMinCanBook.setDate(dateMinCanBook.getDate() + tourDateStartsAfter);
        } else {
            dateMinCanBook.setDate(dateNow.getDate() + tourDateStartsAfter);
        }

        setupBookingFlatpickrLocale(this.state.flatpickrFirstDayOfWeek)
            .then((localeState) => {
                this.state.flatpickrLocale = localeState.localeConfig;
            })
            .catch(() => {
                this.state.flatpickrLocale = null;
            })
            .finally(() => {
                // Setup fixed date picker
                this.setupFixedDatePicker(dateMinCanBook);

                // Setup check-in/check-out pickers
                this.setupCheckInOutPickers(dateMinCanBook);

                // Initial price calculation (after datepickers are set up)
                this.updateTotalPrice();
            });
    }

    /**
     * Setup fixed date picker
     */
    setupFixedDatePicker(minDate) {
        if (!this.elements.inputDateBook) return;

        let duration = this.elements.tourDurationNumber ? (parseInt(this.elements.tourDurationNumber.value) - 1) : 0;
        duration = duration < 0 ? 0 : duration;
        const self = this;

        // Determine the first available date (today if not disabled, otherwise minDate)
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const todayStr = TourBookingManager.convertDateObjToDefault(today);

        let firstAvailableDate = minDate;

        // Check if today is available (not disabled and not before minDate)
        if (today >= minDate) {
            if (!self.state.datesDisable || self.state.datesDisable.indexOf(todayStr) === -1) {
                firstAvailableDate = today;
            }
        }

        this.pickers.fixed = flatpickr(this.elements.inputDateBook, {
            dateFormat: this.state.tourDateFormat,
            locale: this.getPickerLocaleConfig(),
            minDate: minDate,
            maxDate: this.state.maxYear ? new Date(this.state.maxYear, 11, 31) : null,
            defaultDate: firstAvailableDate,

            onDayCreate: (dObj, dStr, fp, dayElem) => {
                const dateStr = TourBookingManager.convertDateObjToDefault(dayElem.dateObj);

                if (self.state.datesDisable && self.state.datesDisable.indexOf(dateStr) > -1) {
                    dayElem.classList.add('flatpickr-disabled');
                }

                if (self.elements.inputDateBook.value !== '') {
                    const startDateObj = new Date(self.convertDateToDefault(self.elements.inputDateBook.value));
                    const endDateObj = new Date(startDateObj.getTime());
                    endDateObj.setDate(startDateObj.getDate() + duration);

                    const rangeDates = [];
                    const dateNext = new Date(startDateObj.getTime());
                    while (dateNext < endDateObj) {
                        rangeDates.push(TourBookingManager.convertDateObjToDefault(dateNext));
                        dateNext.setDate(dateNext.getDate() + 1);
                    }

                    const startDateStr = TourBookingManager.convertDateObjToDefault(startDateObj);
                    const endDateStr = TourBookingManager.convertDateObjToDefault(endDateObj);

                    // If start and end are the same (duration = 0), add both classes
                    if (dateStr === startDateStr && dateStr === endDateStr) {
                        dayElem.classList.add('date-picked', 'start-date', 'end-date');
                    } else if (dateStr === startDateStr) {
                        dayElem.classList.add('date-picked', 'start-date');
                    } else if (dateStr === endDateStr) {
                        dayElem.classList.add('date-picked', 'end-date');
                        // Add end date indicator
                        dayElem.setAttribute('title', 'End Date');
                    } else if (rangeDates.indexOf(dateStr) > -1) {
                        dayElem.classList.add('date-picked');
                    }
                }
            },

            onReady: (selectedDates, dateStr, instance) => {
                // Add custom class to calendar container
                instance.calendarContainer.classList.add("tour-booking-calendar");
                // On initial load, if no date is selected but we have a default, set it
                if (selectedDates.length === 0 || !self.elements.inputDateBook.value) {
                    instance.setDate(firstAvailableDate, true);
                }

                // Trigger the change event to display range info
                if (duration > 0 && selectedDates.length > 0) {
                    const startDate = selectedDates[0];
                    const endDate = new Date(startDate);
                    endDate.setDate(startDate.getDate() + duration);

                    self.elements.inputDateBook.setAttribute('data-end-date', TourBookingManager.convertDateObjToDefault(endDate));
                }

                // Force redraw to apply date-picked classes immediately
                setTimeout(() => {
                    instance.redraw();
                }, 10);
            },

            onChange: (selectedDates, dateStr, instance) => {
                if (selectedDates.length > 0 && duration > 0) {
                    // Calculate and display end date information
                    const startDate = selectedDates[0];
                    const endDate = new Date(startDate);
                    endDate.setDate(startDate.getDate() + duration);

                    // Store end date for reference
                    self.elements.inputDateBook.setAttribute('data-end-date',
                        TourBookingManager.convertDateObjToDefault(endDate));
                }

                // Redraw calendar to show the range
                instance.redraw();
            },

            onClose: () => {
                self.updateTotalPrice();
            }
        });
    }

    /**
     * Setup check-in and check-out pickers
     */
    setupCheckInOutPickers(minDate) {
        if (!this.elements.dateCheckinCheckout) return;

        this.elements.tourDateCheckIn = this.elements.dateCheckinCheckout.querySelector('input[name=tour_date_check_in]');
        this.elements.tourDateCheckOut = this.elements.dateCheckinCheckout.querySelector('input[name=tour_date_check_out]');

        const self = this;

        // Calculate default dates for check-in (minDate) and check-out (day after minDate)
        // IMPORTANT: Do NOT zero hours on defaultCheckIn — minDate retains the current
        // time (e.g. 10:32 AM), and Flatpickr silently rejects defaultDate < minDate.
        // Zeroing hours would make defaultCheckIn (00:00) < minDate (10:32), breaking it.
        const defaultCheckIn = new Date(minDate.getTime());
        const defaultCheckOut = new Date(defaultCheckIn.getTime());
        defaultCheckOut.setDate(defaultCheckOut.getDate() + 1);

        // Skip disabled dates for defaultCheckOut
        if (this.state.datesDisable) {
            let checkOutStr = TourBookingManager.convertDateObjToDefault(defaultCheckOut);
            while (this.state.datesDisable.indexOf(checkOutStr) > -1) {
                defaultCheckOut.setDate(defaultCheckOut.getDate() + 1);
                checkOutStr = TourBookingManager.convertDateObjToDefault(defaultCheckOut);
            }
        }

        if (this.elements.tourDateCheckIn) {
            this.pickers.checkIn = flatpickr(this.elements.tourDateCheckIn, {
                dateFormat: this.state.tourDateFormat,
                locale: this.getPickerLocaleConfig(),
                minDate: minDate,
                maxDate: this.state.maxYear ? new Date(this.state.maxYear, 11, 31) : null,
                defaultDate: defaultCheckIn,
                onDayCreate: (dObj, dStr, fp, dayElem) => {
                    const dateStr = TourBookingManager.convertDateObjToDefault(dayElem.dateObj);

                    if (self.state.datesDisable && self.state.datesDisable.indexOf(dateStr) > -1) {
                        dayElem.classList.add('flatpickr-disabled');
                    }

                    if (self.state.tourDatesChoose && self.state.tourDatesChoose.indexOf(dateStr) > -1) {
                        dayElem.classList.add('date-picked');
                    }
                },
                onReady: function (selectedDates, dateStr, instance) {
                    instance.calendarContainer.classList.add("tour-booking-calendar");
                },
                onClose: (selectedDates) => {
                    self.showRangeDatesChoose();

                    if (selectedDates.length > 0) {
                        const checkInDate = selectedDates[0];
                        const dateNext = new Date(checkInDate);
                        dateNext.setDate(dateNext.getDate() + 1);

                        if (self.pickers.checkOut) {
                            // Set minimum date (day after check-in)
                            self.pickers.checkOut.set('minDate', dateNext);

                            // Find the first blocked date after check-in to set as maxDate
                            let maxCheckOutDate = null;

                            if (self.state.datesDisable && self.state.datesDisable.length > 0) {
                                const checkInDateStr = TourBookingManager.convertDateObjToDefault(checkInDate);

                                // Find all blocked dates after check-in
                                const blockedDatesAfterCheckIn = self.state.datesDisable.filter(dateStr => {
                                    const blockedDate = new Date(dateStr);
                                    return blockedDate > checkInDate;
                                }).sort();

                                if (blockedDatesAfterCheckIn.length > 0) {
                                    // Set maxDate to the day before the first blocked date
                                    const firstBlockedDate = new Date(blockedDatesAfterCheckIn[0]);
                                    maxCheckOutDate = new Date(firstBlockedDate);
                                    maxCheckOutDate.setDate(firstBlockedDate.getDate() - 1);

                                    self.pickers.checkOut.set('maxDate', maxCheckOutDate);
                                } else {
                                    // No blocked dates after check-in, clear maxDate restriction
                                    self.pickers.checkOut.set('maxDate', null);
                                }
                            } else {
                                // No blocked dates at all, clear maxDate restriction
                                self.pickers.checkOut.set('maxDate', null);
                            }

                            const checkOutSelectedDate = self.pickers.checkOut.selectedDates[0];
                            const checkOutFocusDate = checkOutSelectedDate && checkOutSelectedDate >= dateNext
                                ? checkOutSelectedDate
                                : dateNext;

                            self.pickers.checkOut.jumpToDate(checkOutFocusDate, false);
                            self.pickers.checkOut.open();
                        }

                        self.updateTotalPrice();
                    }
                }
            });
        }

        if (this.elements.tourDateCheckOut) {
            this.pickers.checkOut = flatpickr(this.elements.tourDateCheckOut, {
                dateFormat: this.state.tourDateFormat,
                locale: this.getPickerLocaleConfig(),
                minDate: minDate,
                maxDate: this.state.maxYear ? new Date(this.state.maxYear, 11, 31) : null,
                defaultDate: defaultCheckOut,
                onDayCreate: (dObj, dStr, fp, dayElem) => {
                    const dateStr = TourBookingManager.convertDateObjToDefault(dayElem.dateObj);

                    if (self.state.datesDisable && self.state.datesDisable.indexOf(dateStr) > -1) {
                        dayElem.classList.add('flatpickr-disabled');
                    }

                    if (self.state.tourDatesChoose && self.state.tourDatesChoose.indexOf(dateStr) > -1) {
                        dayElem.classList.add('date-picked');
                    }
                },
                onReady: function (selectedDates, dateStr, instance) {
                    instance.calendarContainer.classList.add("tour-booking-calendar");
                },
                onOpen: (selectedDates, dateStr, instance) => {
                    const minDate = instance.config.minDate;
                    const selectedDate = selectedDates[0];
                    const focusDate = selectedDate && (!minDate || selectedDate >= minDate)
                        ? selectedDate
                        : minDate;

                    if (focusDate) {
                        instance.jumpToDate(focusDate, false);
                    }
                },
                onClose: () => {
                    self.showRangeDatesChoose();
                    self.updateTotalPrice();
                }
            });
        }

        // Trigger range highlight and price calculation with default dates
        this.showRangeDatesChoose();
        this.updateTotalPrice();
    }

    /**
     * Show selected date range highlighting
     */
    showRangeDatesChoose() {
        if (!this.elements.tourDateCheckIn || !this.elements.tourDateCheckOut) return;

        const checkInVal = this.elements.tourDateCheckIn.value;
        const checkOutVal = this.elements.tourDateCheckOut.value;

        if (checkInVal !== '' && checkOutVal !== '') {
            this.state.tourDatesChoose = [];
            const startDate = new Date(this.convertDateToDefault(checkInVal));
            const endDate = new Date(this.convertDateToDefault(checkOutVal));
            const dateNext = new Date(startDate.getTime());

            endDate.setDate(endDate.getDate() + 1);

            while (dateNext < endDate) {
                this.state.tourDatesChoose.push(TourBookingManager.convertDateObjToDefault(dateNext));
                dateNext.setDate(dateNext.getDate() + 1);
            }

            if (this.pickers.checkIn) this.pickers.checkIn.redraw();
            if (this.pickers.checkOut) this.pickers.checkOut.redraw();
        }
    }

    // ====================================================================
    // PRICE CALCULATION METHODS
    // ====================================================================

    /**
     * Get default prices for tour tickets
     */
    getDefaultPrices() {
        this.state.priceDatesTour.regular_price_dates = TourBookingManager.safeParseFloat(this.elements.priceTicket?.value, 0);

        if (this.state.tourHaveChildrenTicket && this.elements.priceChildren) {
            this.state.priceDatesTour.child_price_dates = TourBookingManager.safeParseFloat(this.elements.priceChildren.value, 0);
        } else {
            this.state.priceDatesTour.child_price_dates = 0;
        }

        // Process variation prices
        if (this.state.tourVariationOptions) {
            Object.entries(this.state.tourVariationOptions).forEach(([kVar, variation]) => {
                if (variation.set_price == 1 && variation.enable == 1) {
                    if (!this.state.priceDatesTour[kVar]) {
                        this.state.priceDatesTour[kVar] = {};
                    }
                    Object.entries(variation.variation_attr).forEach(([kAttr, attr]) => {
                        this.state.priceDatesTour[kVar][kAttr] = TourBookingManager.safeParseFloat(attr.price, 0);
                    });
                }
            });
        }
    }

    /**
     * Get person counts from form inputs
     */
    getPersonCounts() {
        const totalAdult = TourBookingManager.safeParseInt(this.elements.numberTicket?.value, 0);
        const totalChild = TourBookingManager.safeParseInt(this.elements.numberChildren?.value, 0);
        return { totalAdult, totalChild, totalPerson: totalAdult + totalChild };
    }

    /**
     * Get date range from form inputs
     */
    getDateRange() {
        let checkIn = null, checkOut = null;

        if (this.elements.inputDateBook && this.elements.inputDateBook.value !== '') {
            const duration = TourBookingManager.safeParseFloat(this.elements.tourDurationNumber?.value, 0);
            checkIn = new Date(this.convertDateToDefault(this.elements.inputDateBook.value));
            checkOut = new Date(checkIn.getTime());
            if (duration > 1) {
                checkOut.setDate(checkOut.getDate() + duration);
            } else if (duration === 1) {
                checkOut.setDate(checkOut.getDate() + 1);
            }
        } else if (this.elements.tourDateCheckIn && this.elements.tourDateCheckOut &&
            this.elements.tourDateCheckIn.value !== '' && this.elements.tourDateCheckOut.value !== '') {
            checkIn = new Date(this.convertDateToDefault(this.elements.tourDateCheckIn.value));
            checkOut = new Date(this.convertDateToDefault(this.elements.tourDateCheckOut.value));
        }

        if (!checkIn || !checkOut) return null;
        return { checkIn, checkOut };
    }

    /**
     * Add default prices for a single day to the temp price object
     * @param {Object} priceDatesTourTmp - Accumulated prices
     * @param {Object} priceDatesTour - Default prices
     */
    getPriceDatesDefaultByDateRange(priceDatesTourTmp, priceDatesTour) {
        Object.entries(priceDatesTour).forEach(([k, v]) => {
            if (k === 'regular_price_dates' || k === 'child_price_dates') {
                priceDatesTourTmp[k] = (priceDatesTourTmp[k] || 0) + TourBookingManager.safeParseFloat(v, 0);
            } else if (typeof v === 'object' && v !== null) {
                // Variations
                if (!priceDatesTourTmp[k]) {
                    priceDatesTourTmp[k] = {};
                }
                Object.entries(v).forEach(([kAttr, vAttr]) => {
                    priceDatesTourTmp[k][kAttr] = (priceDatesTourTmp[k][kAttr] || 0) + TourBookingManager.safeParseFloat(vAttr, 0);
                });
            }
        });
    }

    /**
     * Calculate prices for date range (multiplies by number of nights)
     * @param {Date} dateCheckIn - Check-in date
     * @param {Date} dateCheckOut - Check-out date
     */
    calculatePricesForDateRange(dateCheckIn, dateCheckOut) {
        const hasPriceDateOption = this.state.tourPriceOfDatesOption &&
            this.state.tourPriceOfDatesOption.price_dates_range &&
            Object.keys(this.state.tourPriceOfDatesOption.price_dates_range).length > 0;

        const priceDatesTourTmp = {};
        let dateNext = new Date(dateCheckIn.getTime());

        // Calculate total nights and update display
        const totalNight = TourBookingManager.calculateTotalNights(dateCheckIn, dateCheckOut);
        const elNumberDay = document.querySelector('.number-day');
        if (elNumberDay) {
            elNumberDay.textContent = totalNight;
        }

        // Calculate prices for each day in the range
        while (dateNext < dateCheckOut) {
            if (hasPriceDateOption) {
                let dateMatchedOption = false;

                Object.entries(this.state.tourPriceOfDatesOption.price_dates_range).forEach(([kRange, priceRange]) => {
                    const startDate = new Date(priceRange.start_date);
                    const endDate = new Date(priceRange.end_date);

                    if (dateNext >= startDate && dateNext <= endDate) {
                        dateMatchedOption = true;

                        Object.entries(priceRange.prices).forEach(([kPrice, vPrice]) => {
                            if (this.state.priceDatesTour[kPrice] !== undefined) {
                                if (kPrice === 'regular_price_dates' || kPrice === 'child_price_dates') {
                                    let priceAttr = TourBookingManager.safeParseFloat(vPrice.price, 0);
                                    if (priceAttr <= 0) {
                                        priceAttr = TourBookingManager.safeParseFloat(this.state.priceDatesTour[kPrice], 0);
                                    }
                                    priceDatesTourTmp[kPrice] = (priceDatesTourTmp[kPrice] || 0) + priceAttr;
                                } else {
                                    // Variations
                                    Object.entries(vPrice).forEach(([kVar, varAttr]) => {
                                        if (this.state.priceDatesTour[kPrice] && this.state.priceDatesTour[kPrice][kVar] !== undefined) {
                                            let priceAttr = TourBookingManager.safeParseFloat(varAttr.price, 0);
                                            if (priceAttr <= 0) {
                                                priceAttr = TourBookingManager.safeParseFloat(this.state.priceDatesTour[kPrice][kVar], 0);
                                            }
                                            if (!priceDatesTourTmp[kPrice]) {
                                                priceDatesTourTmp[kPrice] = {};
                                            }
                                            priceDatesTourTmp[kPrice][kVar] = (priceDatesTourTmp[kPrice][kVar] || 0) + priceAttr;
                                        }
                                    });
                                }
                            }
                        });
                    }
                });

                // Use default price if date not in any option range
                if (!dateMatchedOption) {
                    this.getPriceDatesDefaultByDateRange(priceDatesTourTmp, this.state.priceDatesTour);
                }
            } else {
                // No price date options - use regular prices
                this.getPriceDatesDefaultByDateRange(priceDatesTourTmp, this.state.priceDatesTour);
            }

            dateNext.setDate(dateNext.getDate() + 1);
        }

        // Update priceDatesTour with calculated values (with fallback to 0)
        this.state.priceDatesTour.regular_price_dates = priceDatesTourTmp.regular_price_dates || 0;
        this.state.priceDatesTour.child_price_dates = priceDatesTourTmp.child_price_dates || 0;

        // Update variation prices
        Object.entries(this.state.priceDatesTour).forEach(([kPrice, vPrice]) => {
            if (kPrice !== 'regular_price_dates' && kPrice !== 'child_price_dates' && typeof vPrice === 'object') {
                Object.entries(vPrice).forEach(([kAttr]) => {
                    if (priceDatesTourTmp[kPrice] && priceDatesTourTmp[kPrice][kAttr] !== undefined) {
                        this.state.priceDatesTour[kPrice][kAttr] = priceDatesTourTmp[kPrice][kAttr];
                    }
                });
            }
        });
    }

    /**
     * Calculate subtotal from prices
     */
    calculateSubtotal(totalAdult, totalChild) {
        let subtotal = 0;
        const prices = this.state.priceDatesTour;

        Object.entries(prices).forEach(([k, v]) => {
            if (k === 'regular_price_dates') {
                const priceValue = TourBookingManager.safeParseFloat(prices[k], 0);
                subtotal += priceValue * totalAdult;
                if (this.elements.showPriceTicket) {
                    this.elements.showPriceTicket.textContent = TourBookingManager.formatPrice(priceValue);
                }
            } else if (k === 'child_price_dates') {
                const priceValue = TourBookingManager.safeParseFloat(prices[k], 0);
                subtotal += priceValue * totalChild;
                if (this.elements.showPriceChildren) {
                    this.elements.showPriceChildren.textContent = TourBookingManager.formatPrice(priceValue);
                }
            } else if (typeof v === 'object' && v !== null) {
                // Handle variations
                Object.entries(v).forEach(([kVariation]) => {
                    const priceVariation = TourBookingManager.safeParseFloat(prices[k][kVariation], 0);

                    if (this.state.tourVariationOptions && this.state.tourVariationOptions[k]) {
                        switch (this.state.tourVariationOptions[k].type_variation) {
                            case 'quantity': {
                                const qtyInput = document.querySelector(`input[name="${kVariation}"]`);
                                const qtyVariation = TourBookingManager.safeParseInt(qtyInput?.value, 0);
                                subtotal += priceVariation * qtyVariation;
                                break;
                            }
                            case 'checkbox': {
                                const checkboxEl = document.querySelector(`input[name="${kVariation}"]`);
                                if (checkboxEl && checkboxEl.checked) {
                                    subtotal += priceVariation;
                                }
                                break;
                            }
                            case 'radio': {
                                const radioEl = document.querySelector(`input[name="${k}"]:checked`);
                                if (radioEl && radioEl.value === kVariation) {
                                    subtotal += priceVariation;
                                }
                                break;
                            }
                            case 'select': {
                                const selectEl = document.querySelector(`select[name="${k}"]`);
                                const selectedVal = selectEl?.value;
                                if (selectedVal === kVariation) {
                                    subtotal += priceVariation;
                                }
                                break;
                            }
                        }
                    }

                    // Display variation price
                    const priceEl = document.querySelector(`.price_${kVariation}`);
                    if (priceEl) {
                        priceEl.textContent = TourBookingManager.formatPrice(priceVariation);
                    }
                });
            }
        });

        return subtotal;
    }

    /**
     * Apply group discount to subtotal
     */
    applyGroupDiscount(subtotal, totalPerson) {
        if (!this.state.tourGroupDiscountData) return subtotal;

        const discountLabelEl = this.elements.labelTourGroupDiscount;
        const discountValueEl = discountLabelEl?.querySelector('.val-discount');
        const discountCurrencyEl = discountLabelEl?.querySelector('.woocommerce-Price-currencySymbol');
        const totalPeopleEl = discountLabelEl?.querySelector('.total-people');

        try {
            let customerMatch = 0;
            let discountVal = 0;

            const discountData = Array.isArray(this.state.tourGroupDiscountData)
                ? this.state.tourGroupDiscountData
                : Object.values(this.state.tourGroupDiscountData);

            discountData.forEach((v) => {
                const numCustomer = TourBookingManager.safeParseInt(v.number_customer, 0);
                if (numCustomer <= totalPerson && customerMatch < numCustomer) {
                    customerMatch = numCustomer;
                    discountVal = v.discount;
                }
            });

            if (customerMatch !== 0) {
                if (discountValueEl) {
                    discountValueEl.textContent = discountVal;
                }
                if (totalPeopleEl) {
                    totalPeopleEl.textContent = totalPerson;
                }
                if (discountLabelEl) {
                    discountLabelEl.style.display = '';
                }

                const isPercentage = /\%$/.test(discountVal);
                if (isPercentage) {
                    if (discountCurrencyEl) {
                        discountCurrencyEl.style.display = 'none';
                    }
                    const percent = TourBookingManager.safeParseFloat(discountVal, 0);
                    subtotal = subtotal - (subtotal * percent / 100);
                } else {
                    const amount = TourBookingManager.safeParseFloat(discountVal, 0);
                    if (discountValueEl) {
                        discountValueEl.textContent = TourBookingManager.formatPrice(amount);
                    }
                    if (discountCurrencyEl) {
                        discountCurrencyEl.style.display = '';
                    }
                    subtotal = subtotal - amount;
                }
            } else if (discountLabelEl) {
                discountLabelEl.style.display = 'none';
            }
        } catch (e) {
            console.error('Error applying discount:', e);
        }

        return subtotal;
    }

    /**
     * Display the final total price
     */
    displayTotalPrice(subtotal) {
        const finalSubtotal = isNaN(subtotal) ? 0 : subtotal;
        if (this.elements.showTotalPrice) {
            this.elements.showTotalPrice.textContent = TourBookingManager.formatPrice(finalSubtotal);
        }
    }

    /**
     * Apply deposit calculation if deposit is selected
     */
    applyDepositToSubtotal(subtotal) {
        // AWCDP (Deposits & Partial Payments for WooCommerce - Acowebs)
        const awcdpWrapper = document.querySelector('.awcdp-deposits-wrapper');
        if (awcdpWrapper) {
            const awcdpRadio = document.querySelector('input[name="awcdp_deposit_option"]:checked');
            if (!awcdpRadio || awcdpRadio.value !== 'yes') return subtotal;

            const depositType = awcdpWrapper.dataset.depositType;
            const depositAmount = parseFloat(awcdpWrapper.dataset.depositAmount) || 0;

            if (depositType === 'percent') {
                return subtotal * depositAmount / 100;
            } else if (depositType === 'fixed') {
                return Math.min(depositAmount, subtotal);
            }
            return subtotal;
        }

        // WC Deposits (official)
        const depositWrapper = document.querySelector('.tour-deposit-option');
        if (!depositWrapper) return subtotal;

        const depositRadio = document.querySelector('input[name="wc_deposit_option"]:checked');
        if (!depositRadio || depositRadio.value !== 'deposit') return subtotal;

        const depositType = depositWrapper.dataset.depositType;
        const depositAmount = parseFloat(depositWrapper.dataset.depositAmount) || 0;

        if (depositType === 'percent') {
            return subtotal * depositAmount / 100;
        } else if (depositType === 'fixed') {
            return Math.min(depositAmount, subtotal);
        } else if (depositType === 'plan') {
            const planSchedules = JSON.parse(depositWrapper.dataset.planSchedules || '{}');
            const selectedPlan = document.querySelector('input[name="wc_deposit_payment_plan"]:checked');

            if (selectedPlan && planSchedules[selectedPlan.value]) {
                const schedule = planSchedules[selectedPlan.value];
                const totalPercent = schedule.total_percent || 100;
                const firstPercent = schedule.first_percent || 0;
                // Full amount adjusted by plan surcharge, then first payment %
                const planFullAmount = (subtotal / 100) * totalPercent;
                return (planFullAmount / 100) * firstPercent;
            }
        }

        return subtotal;
    }

    /**
     * Main price calculation method
     */
    updateTotalPrice() {
        // Re-query elements that may have been null due to async loading
        this._refreshElements();

        this.getDefaultPrices();
        const { totalAdult, totalChild, totalPerson } = this.getPersonCounts();

        const dateRange = this.getDateRange();
        if (!dateRange) return;

        // Calculate prices for the date range (multiplies base prices by nights)
        this.calculatePricesForDateRange(dateRange.checkIn, dateRange.checkOut);

        let subtotal = this.calculateSubtotal(totalAdult, totalChild);

        subtotal = this.applyGroupDiscount(subtotal, totalPerson);

        // Apply deposit calculation if deposit option is selected
        subtotal = this.applyDepositToSubtotal(subtotal);

        this.displayTotalPrice(subtotal);
    }

    // ====================================================================
    // FORM SUBMISSION METHODS
    // ====================================================================

    /**
     * Setup form submission handler via event delegation.
     * Uses document-level delegation so the .btn-booking button
     * is found regardless of async DOM parsing timing.
     */
    setupFormSubmission() {
        const self = this;

        document.addEventListener('click', (e) => {
            const btn = e.target.closest('.btn-booking');
            if (!btn) return;

            e.preventDefault();
            self.submitBooking(btn);
        });
    }

    /**
     * Collect variations data from form inputs
     * Matches the format from booking.js lines 750-864
     * @returns {Object} Variations data object in format: {variation_id: {attr_id: {quantity: X}}}
     */
    collectVariationsData() {
        if (!this.state.tourVariationOptions) return null;

        const variations = {};

        Object.entries(this.state.tourVariationOptions).forEach(([variationKey, variation]) => {
            if (variation.enable != 1) return;

            switch (variation.type_variation) {
                case 'quantity': {
                    // For quantity variations, collect the numeric value
                    Object.keys(variation.variation_attr).forEach(attrKey => {
                        const qtyInput = document.querySelector(`input[name="${attrKey}"]`);
                        if (qtyInput) {
                            const qty = TourBookingManager.safeParseFloat(qtyInput.value, 0);
                            if (qty > 0) {
                                if (!variations[variationKey]) {
                                    variations[variationKey] = {};
                                }
                                variations[variationKey][attrKey] = {
                                    quantity: qty
                                };
                            }
                        }
                    });
                    break;
                }

                case 'checkbox': {
                    // For checkboxes, collect all checked values
                    Object.keys(variation.variation_attr).forEach(attrKey => {
                        const checkbox = document.querySelector(`input[name="${attrKey}"]`);
                        if (checkbox && checkbox.checked) {
                            if (!variations[variationKey]) {
                                variations[variationKey] = {};
                            }
                            const attrValue = parseInt(checkbox.value) || parseInt(attrKey);
                            variations[variationKey][attrValue] = {};
                        }
                    });
                    break;
                }

                case 'radio': {
                    // For radio buttons, collect the selected value
                    const radioSelected = document.querySelector(`input[name="${variationKey}"]:checked`);
                    if (radioSelected) {
                        if (!variations[variationKey]) {
                            variations[variationKey] = {};
                        }
                        const attrValue = parseInt(radioSelected.value);
                        variations[variationKey][attrValue] = {};
                    }
                    break;
                }

                case 'select': {
                    // For select dropdowns, collect the selected value
                    const selectEl = document.querySelector(`select[name="${variationKey}"]`);
                    if (selectEl && selectEl.value) {
                        const attrValue = parseInt(selectEl.value);
                        if (attrValue > 0) {
                            if (!variations[variationKey]) {
                                variations[variationKey] = {};
                            }
                            variations[variationKey][attrValue] = {};
                        }
                    }
                    break;
                }
            }
        });

        return Object.keys(variations).length > 0 ? variations : null;
    }

    /**
     * Validate email format
     */
    validateEmail(email) {
        const regex = /^([a-zA-Z0-9_.+-])+\@(([a-zA-Z0-9-])+\.)+([a-zA-Z0-9]{2,4})+$/;
        return regex.test(email);
    }

    /**
     * Collect all extra form data (variations, custom fields, extras, etc.)
     * @param {FormData} formData - FormData object to append to
     * @param {Object} excludeFields - Object containing field names to exclude
     */
    collectExtraFormData(formData, excludeFields) {
        if (!this.elements.form) {
            console.warn('Form element not found');
            return;
        }

        const formElements = this.elements.form.elements;
        const userInfoFields = ['first_name', 'last_name', 'email_tour', 'phone'];

        // Config fields that shouldn't be submitted
        const configFields = [
            'phys_tour_dates_disable',
            'phys_tour_price_dates_type',
            'phys_tour_price_of_dates_option',
            'phys_tour_max_year_enable',
            '_tour_variations_options',
            'tour_group_discount_data_phys',
            'tour_date_starts_after',
            'tour_duration_number',
            'tour_month',
            'woocommerce_price_num_decimals',
            'url_home',
            'checkout_url',
            'price_ticket',
            'price_children'
        ];

        // Get all variation field names to exclude them
        const variationFieldNames = [];
        if (this.state.tourVariationOptions) {
            Object.entries(this.state.tourVariationOptions).forEach(([variationKey, variation]) => {
                variationFieldNames.push(variationKey); // For select/radio
                if (variation.variation_attr) {
                    Object.keys(variation.variation_attr).forEach(attrKey => {
                        variationFieldNames.push(attrKey); // For checkbox/quantity inputs
                    });
                }
            });
        }

        for (let i = 0; i < formElements.length; i++) {
            const element = formElements[i];
            const name = element.name;

            // Skip if no name or already added
            if (!name || excludeFields.hasOwnProperty(name)) continue;

            // Skip user info fields (will be stored in cookie)
            if (userInfoFields.indexOf(name) > -1) continue;

            // Skip config fields
            if (configFields.indexOf(name) > -1) continue;

            // Skip variation fields (handled by collectVariationsData)
            if (variationFieldNames.indexOf(name) > -1) continue;

            // Handle different input types (for custom fields)
            if (element.type === 'checkbox') {
                if (element.checked) {
                    formData.append(name, element.value);
                }
            } else if (element.type === 'radio') {
                if (element.checked) {
                    formData.append(name, element.value);
                }
            } else if (element.tagName.toLowerCase() === 'select') {
                if (element.multiple) {
                    // Handle multi-select
                    for (let j = 0; j < element.selectedOptions.length; j++) {
                        formData.append(name + '[]', element.selectedOptions[j].value);
                    }
                } else {
                    formData.append(name, element.value);
                }
            } else if (element.tagName.toLowerCase() === 'textarea' || element.tagName.toLowerCase() === 'input') {
                // Handle text inputs, textareas, hidden fields, etc.
                if (element.value) {
                    formData.append(name, element.value);
                }
            }
        }
    }

    /**
     * Submit booking form
     */
    submitBooking(btnElement) {
        // Get form inputs
        const inputFirstName = document.querySelector('input[name=first_name]');
        const inputLastName = document.querySelector('input[name=last_name]');
        const inputEmail = document.querySelector('input[name=email_tour]');
        const inputPhone = document.querySelector('input[name=phone]');
        const inputQty = this.elements.numberTicket;
        const urlHomeInput = document.querySelector('input[name=url_home]');
        const ajaxUrl = urlHomeInput ? urlHomeInput.value : '';

        // Validation
        if (inputFirstName && inputFirstName.value.length === 0) {
            inputFirstName.classList.add('error');
            return;
        }
        if (inputLastName && inputLastName.value.length === 0) {
            inputLastName.classList.add('error');
            return;
        }
        if (inputEmail && !this.validateEmail(inputEmail.value)) {
            inputEmail.classList.add('error');
            return;
        }
        if (inputPhone && inputPhone.value.length === 0) {
            inputPhone.classList.add('error');
            return;
        }

        if (btnElement.classList.contains('disable')) return;

        // Build basic data
        const nonceInput = document.querySelector('input[name=nonce]');
        const tourIdInput = document.querySelector('input[name=tour_id]');

        const data = {
            nonce: nonceInput ? nonceInput.value : '',
            tour_id: tourIdInput ? tourIdInput.value : '',
            date_booking: this.elements.inputDateBook ? this.elements.inputDateBook.value : '',
            number_ticket: inputQty ? inputQty.value : '1',
        };

        // Add children count if applicable
        if (this.elements.numberChildren) {
            data.number_children = this.elements.numberChildren.value || 0;
        }

        // Add check-in and check-out dates if using date range picker
        // Note: PHP expects 'date_check_in' and 'date_check_out' (not 'tour_date_check_in/out')
        if (this.elements.tourDateCheckIn && this.elements.tourDateCheckIn.value) {
            data.date_check_in = this.elements.tourDateCheckIn.value;
        }
        if (this.elements.tourDateCheckOut && this.elements.tourDateCheckOut.value) {
            data.date_check_out = this.elements.tourDateCheckOut.value;
        }

        // Collect variations data
        const variations = this.collectVariationsData();
        if (variations && Object.keys(variations).length > 0) {
            data.tour_variations = JSON.stringify(variations);
        }

        // Show spinner and disable button
        const spinner = document.querySelector('.spinner');
        if (spinner) spinner.style.display = 'block';
        btnElement.classList.add('disable');

        // Build FormData and include all form fields
        const formData = new FormData();

        // Add basic data
        Object.entries(data).forEach(([key, value]) => {
            formData.append(key, value);
        });

        // Collect all extra form data (variations, custom fields, extras, etc.)
        this.collectExtraFormData(formData, data);

        // Submit
        fetch(ajaxUrl + '?tb-ajax=add_tour_to_cart_phys', {
            method: 'POST',
            body: formData
        })
            .then(response => response.json())
            .then(result => {
                if (result.status === 'success') {
                    const userInfo = {
                        first_name: inputFirstName ? inputFirstName.value : '',
                        last_name: inputLastName ? inputLastName.value : '',
                        email: inputEmail ? inputEmail.value : '',
                        phone: inputPhone ? inputPhone.value : '',
                    };
                    document.cookie = 'info_user=' + JSON.stringify(userInfo) + '; path=/';

                    const checkoutUrl = document.querySelector('input[name=checkout_url]');
                    if (checkoutUrl) {
                        window.location = checkoutUrl.value;
                    }
                } else {
                    alert(result.message);
                }
            })
            .catch(error => console.error(error))
            .finally(() => {
                if (spinner) spinner.style.display = 'none';
                btnElement.classList.remove('disable');
            });
    }
}

// ====================================================================
// INITIALIZATION
// ====================================================================
travelUtils.onElementReady(
    'form#tourBookingForm',
    (elForm) => {
        const manager = new TourBookingManager();
        manager.init();

        // Make available globally for debugging
        window.tourBookingManager = manager;
    }
);
/*document.addEventListener('DOMContentLoaded', () => {
    const manager = new TourBookingManager();
    manager.init();

    // Make available globally for debugging
    window.tourBookingManager = manager;
} );*/

travelUtils.onElementReady(
    'input[name=billing_first_name]',
    ( el ) => {
        const manager = new TourBookingManager();
        manager.init();
        manager._fillBillingInfo();

        // Make available globally for debugging
        window.tourBookingManager = manager;
    }
);
